import type { StyleLibrary } from "schemas/generated/project";

import React, { createContext } from "react";

import { useLocation, useSearchParams } from "react-router-dom";
import { useRequiredContext } from "replo-utils/react/context";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

type PanelView =
  | "chat"
  | "history"
  | "designLibrary"
  | "pageSettings"
  | "manageChats";

interface EditorStore {
  panelView: PanelView;
  draftStyleLibrary: StyleLibrary | null;
  mobileChatMode: "chat" | "preview";
  viewingVersionId: string | null;
  isPanelResizing: boolean;

  actions: {
    setPanelView: (panelView: PanelView) => void;
    setMobileChatMode: (mode: "chat" | "preview") => void;
    setViewingVersionId: (viewingVersionId: string | null) => void;
    setDraftStyleLibrary: (draftStyleLibrary: StyleLibrary | null) => void;
    setIsPanelResizing: (isPanelResizing: boolean) => void;
  };
}

const createEditorStore = ({
  initialFileVersionId,
}: {
  initialFileVersionId: string | null;
}) => {
  return createStore<EditorStore>((set) => ({
    panelView: initialFileVersionId ? "history" : "chat",
    mobileChatMode: "chat",
    viewingVersionId: initialFileVersionId,
    draftStyleLibrary: null,
    isPanelResizing: false,
    actions: {
      setPanelView: (newPanelView: PanelView) => {
        if (newPanelView === "chat") {
          localStorage.setItem(
            "replo.dashboard.sidebarExpanded",
            JSON.stringify(false),
          );
        }
        set((currentState) => {
          const isLeavingHistory =
            currentState.panelView === "history" && newPanelView !== "history";

          return {
            panelView: newPanelView,
            viewingVersionId: isLeavingHistory
              ? null
              : currentState.viewingVersionId,
            draftStyleLibrary:
              newPanelView === "designLibrary"
                ? currentState.draftStyleLibrary
                : null,
          };
        });
      },
      setMobileChatMode: (mode: "chat" | "preview") => {
        return set({ mobileChatMode: mode });
      },
      setViewingVersionId: (viewingVersionId: string | null) => {
        return set({ viewingVersionId });
      },
      setDraftStyleLibrary: (draftStyleLibrary: StyleLibrary | null) => {
        return set({ draftStyleLibrary });
      },
      setIsPanelResizing: (isPanelResizing: boolean) => {
        return set({ isPanelResizing });
      },
    },
  }));
};

type EditorStoreContextType = {
  store: ReturnType<typeof createEditorStore>;
};

export const EditorStoreContext = createContext<EditorStoreContextType | null>(
  null,
);

const useEditorStoreContext = () => {
  return useRequiredContext(EditorStoreContext);
};

export function EditorStoreProvider({ children }: React.PropsWithChildren) {
  const [searchParams] = useSearchParams();
  const initialFileVersionId = searchParams.get("initialFileVersionId");

  const [store] = React.useState(() => {
    return createEditorStore({ initialFileVersionId });
  });

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  const location = useLocation();

  useOnValueChange(location, (location, previousLocation) => {
    if (
      location.pathname.endsWith("/chat") &&
      !previousLocation?.pathname.endsWith("/chat")
    ) {
      store.setState({ panelView: "chat" });
    }
  });

  return (
    <EditorStoreContext.Provider value={contextValue}>
      {children}
    </EditorStoreContext.Provider>
  );
}

function useEditorStore<SelectorReturnType>(
  selector: (state: EditorStore) => SelectorReturnType,
): SelectorReturnType {
  const { store } = useEditorStoreContext();
  return useShallowStore(store, selector);
}

export const usePanelView = () => useEditorStore((state) => state.panelView);
export const useMobileChatMode = () => {
  return useEditorStore((state) => state.mobileChatMode);
};
export const useViewingVersionId = () => {
  return useEditorStore((state) => state.viewingVersionId);
};

// NOTE (Bobby: 2025-07-09): Creating just one selector for all actions since actions are static and never change
// so regardless they don't cause any re-renders or impact performance.
export const useEditorStoreActions = () => {
  return useEditorStore((state) => state.actions);
};

export const useEditorDraftStyleLibrary = () => {
  return useEditorStore((state) => state.draftStyleLibrary);
};

export const useIsPanelResizing = () => {
  return useEditorStore((state) => state.isPanelResizing);
};
